import React from 'react';
import {
  CreateProjectDetailsFieldRequest,
  ProjectDetailsField,
  ProjectType, TeamPermission,
  useProjectDetailsFieldsService,
} from 'nekst-api';
import ProjectDetailsFieldTypeDropdown from './ProjectDetailsFieldTypeDropdown';
import {
  AfterSubmitFunc,
  Button,
  ButtonColor,
  ButtonSize,
  ButtonVariant,
  FormTemplate,
  IconType,
  OptionSelectionButton,
  TextInput,
} from 'ui-builder';
import { usePopupFormManager } from 'features/nekst-widgets';
import useFeatureToggleService from 'feature-toggle';
import CreateDetailsFieldsSetForm from '../Sets/CreateUpdate/CreateDetailsFieldsSetForm';
import { useAuthorizationChecker } from 'authorization-scope';
import {
  useTeamSetupAreaContext,
} from '../../markets/TeamSetupAreaContext';
import { useSetupAreaLabelsHelper } from '../../markets/setupAreaLabelsHelper';

function CreateProjectDetailsFieldForm(
  props: {
    projectType: ProjectType,
    afterSubmitFunc?: AfterSubmitFunc,
  },
) {
  const projectDetailsFieldsService = useProjectDetailsFieldsService();

  const labelsHelper = useSetupAreaLabelsHelper();

  return (
    <FormTemplate<CreateProjectDetailsFieldRequest, ProjectDetailsField>
      title="Add Detail Box"
      submitFormFunc={(request: CreateProjectDetailsFieldRequest) => {
        return projectDetailsFieldsService.createField(props.projectType, request);
      }}
      afterSubmitFunc={props.afterSubmitFunc}
      validationSchema={{
        name: {
          type: 'string',
          constraints: {
            required: true,
            maxLength: 40,
          },
        },
        fieldType: {
          type: 'string',
          constraints: {
            required: true,
          },
        },
      }}
    >
      <TextInput source="name" label="Name" />
      <ProjectDetailsFieldTypeDropdown
        source="fieldType"
        label={labelsHelper.getFieldTypeLabel()}
      />
    </FormTemplate>
  );
}

export default function CreateProjectDetailsFieldButton(props: {
  projectType: ProjectType,
}) {
  const popupManager = usePopupFormManager();

  const featureToggleService = useFeatureToggleService();

  const areaContext = useTeamSetupAreaContext();

  const { isGranted } = useAuthorizationChecker();

  if (featureToggleService.isDetailsFieldSetsEnabled()
    && isGranted(TeamPermission.MANAGE_AI_TRAINING_DATA)
    && (!areaContext || areaContext.teamSetupArea.alias === 'internal')
  ) {
    return (
      <OptionSelectionButton
        text="Add"
        options={[
          {
            label: 'Details Box',
            value: 'details_box',
            onSelected: () => {
              popupManager.openForm(
                <CreateProjectDetailsFieldForm projectType={props.projectType} />,
              );
            }
          },
          {
            label: 'Detail Box Set',
            value: 'details_box_set',
            onSelected: () => {
              popupManager.openForm(
                <CreateDetailsFieldsSetForm projectType={props.projectType} />,
              );
            }
          }
        ]}
        size={ButtonSize.MEDIUM}
      />
    );
  } else {
    return (
      <Button
        text="Add Detail Box"
        color={ButtonColor.BLUE}
        iconType={IconType.PLUS}
        size={ButtonSize.MEDIUM}
        variant={ButtonVariant.FILLED}
        onClick={() => {
          popupManager.openForm(
            <CreateProjectDetailsFieldForm projectType={props.projectType} />,
          );
        }}
      />
    );
  }
}
